import React, { useEffect, useMemo, useState } from "react";
import { API_URL } from "./config";
import DashboardLayout from "./DashboardLayout/DasboardLayout";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

const APILINK = API_URL;
const DEFAULT_MESSAGE =
  "Hi XGO users, we’ve improved WhatsApp booking, driver details, trip tracking, and web app access over the last two weeks. We’ve also added clearer terms and a Guide/Help command. Reply HELP or chat here: https://wa.me/263XXXXXXXXX";

const defaultFilters = {
  userType: "riders",
  activityWindow: "30",
  serviceType: "all",
  verifiedOnly: true,
  activeOnly: true,
  optInOnly: true,
  excludeOptedOut: true,
  schedule: "now",
};

const normalizePhone = (value) => {
  if (!value) return "";
  return String(value).replace(/[^\d+]/g, "").trim();
};

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeCustomer = (customer, index) => {
  const name =
    customer?.name ||
    customer?.username ||
    customer?.full_name ||
    customer?.customer_name ||
    `Customer ${index + 1}`;

  const phone = normalizePhone(customer?.phone || customer?.mobile || customer?.phone_number);
  const userType = String(
    customer?.role ||
      customer?.user_type ||
      customer?.account_type ||
      customer?.customer_type ||
      "rider"
  )
    .toLowerCase();

  const lastRide =
    customer?.last_trip_date ||
    customer?.last_active_at ||
    customer?.last_seen ||
    customer?.last_request_date ||
    customer?.updated_at ||
    customer?.created_at ||
    "";

  const status = customer?.status || customer?.account_status || "active";
  const verified =
    customer?.verified ??
    customer?.is_verified ??
    customer?.phone_verified ??
    customer?.verified_phone ??
    true;
  const active = customer?.active ?? customer?.is_active ?? status === "active" ?? true;
  const smsConsent =
    customer?.sms_opt_in ??
    customer?.sms_consent ??
    customer?.sms_enabled ??
    customer?.marketing_opt_in ??
    true;

  return {
    id: customer?.id || customer?.customerid || index,
    name,
    phone,
    userType,
    city: customer?.city || customer?.location || "—",
    lastRide,
    status: String(status),
    verified: Boolean(verified),
    active: Boolean(active),
    smsConsent: smsConsent === false || String(smsConsent).toLowerCase() === "false" ? false : true,
  };
};

const BroadcastCust = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [customers, setCustomers] = useState([]);
  const [filters, setFilters] = useState(defaultFilters);

  const getCustomers = async () => {
    try {
      const response = await fetch(`${APILINK}/customerdetails/`);
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      const data = await response.json();
      const normalized = Array.isArray(data) ? data.map(normalizeCustomer) : [];
      setCustomers(normalized.filter((customer) => customer.phone));
    } catch (error) {
      console.log("Failed to fetch customers:", error);
      toast.error("Could not load customer list.");
    }
  };

  const filteredRecipients = useMemo(() => {
    const selected = customers.filter((customer) => {
      if (!customer.phone) return false;

      if (filters.userType === "riders") {
        const isRider = ["rider", "customer", "passenger"].includes(customer.userType);
        if (!isRider) return false;
      }

      if (filters.userType === "drivers") {
        if (customer.userType !== "driver") return false;
      }

      if (filters.verifiedOnly && !customer.verified) return false;
      if (filters.activeOnly && !customer.active) return false;
      if (filters.optInOnly && !customer.smsConsent) return false;
      if (filters.excludeOptedOut && !customer.smsConsent) return false;

      const activityWindow = Number(filters.activityWindow || "0");
      if (activityWindow > 0) {
        const lastRideDate = parseDate(customer.lastRide);
        if (lastRideDate) {
          const diffDays = (Date.now() - lastRideDate.getTime()) / 86400000;
          if (diffDays > activityWindow) return false;
        }
      }

      return true;
    });

    return selected;
  }, [customers, filters]);

  const previewRecipients = filteredRecipients.slice(0, 10);
  const messageSegments = Math.max(1, Math.ceil(message.length / 160));
  const estimatedCost = filteredRecipients.length * 0.08;

  const sendSmsBroadcast = async () => {
    const recipients = filteredRecipients.map((customer) => customer.phone);
    if (!recipients.length) {
      toast.error("No matching recipients are available for this audience.");
      return;
    }

    try {
      const response = await fetch(`${APILINK}/notifications/broadcast-sms`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message,
          recipients,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error sending broadcast SMS:", response.status, errorText);
        toast.error("Failed to send broadcast SMS.");
        return false;
      }

      toast.success("Message sent successfully!");
      return true;
    } catch (error) {
      console.error("Network Error:", error);
      toast.error("Could not send broadcast SMS. Please check your connection.");
      return false;
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    await sendSmsBroadcast();
    setMessage(DEFAULT_MESSAGE);
    await getCustomers();
  };

  useEffect(() => {
    getCustomers();
  }, []);

  const updateFilter = (key, value) => {
    setFilters((previous) => ({ ...previous, [key]: value }));
  };

  return (
    <DashboardLayout>
      <div style={{ background: "#f4f7fb", minHeight: "100vh", padding: "24px 20px 40px" }}>
        <Container fluid>
          <Row className="mb-4 align-items-center">
            <Col xs={12}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#002966",
                    fontSize: 22,
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                </button>
                <h2
                  style={{
                    margin: 0,
                    color: "#0d1b2a",
                    fontSize: "2rem",
                    fontWeight: 700,
                    flex: 1,
                    textAlign: "center",
                  }}
                >
                  Broadcast SMS
                </h2>
                <div style={{ minWidth: 120, display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <Button variant="outline-primary" size="sm">Save Draft</Button>
                  <Button variant="primary" size="sm">Send Test</Button>
                </div>
              </div>
            </Col>
          </Row>

          <Row className="g-4">
            <Col lg={4}>
              <div
                style={{
                  background: "#ffffff",
                  borderRadius: 18,
                  border: "1px solid #e8edf5",
                  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.04)",
                  padding: 20,
                }}
              >
                <h5 style={{ color: "#12233d", fontWeight: 700, marginBottom: 18 }}>Audience Filters</h5>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>Campaign Type</Form.Label>
                  <Form.Select value={filters.campaignType || "service_update"} onChange={(e) => updateFilter("campaignType", e.target.value)}>
                    <option value="service_update">Service Update</option>
                    <option value="promotion">Promotion</option>
                    <option value="reminder">Reminder</option>
                    <option value="support">Support Alert</option>
                    <option value="custom">Custom</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>User Type</Form.Label>
                  <div style={{ display: "flex", gap: 8 }}>
                    {[
                      { label: "Riders", value: "riders" },
                      { label: "Drivers", value: "drivers" },
                      { label: "Both", value: "all" },
                    ].map((option) => (
                      <Button
                        key={option.value}
                        type="button"
                        size="sm"
                        variant={filters.userType === option.value ? "primary" : "outline-secondary"}
                        style={{ flex: 1 }}
                        onClick={() => updateFilter("userType", option.value)}
                      >
                        {option.label}
                      </Button>
                    ))}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>Service Type</Form.Label>
                  <Form.Select value={filters.serviceType} onChange={(e) => updateFilter("serviceType", e.target.value)}>
                    <option value="all">All</option>
                    <option value="private">Private Ride</option>
                    <option value="rideshare">Shared Ride</option>
                    <option value="delivery">Delivery</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>Activity Window</Form.Label>
                  <Form.Select value={filters.activityWindow} onChange={(e) => updateFilter("activityWindow", e.target.value)}>
                    <option value="7">Last 7 days</option>
                    <option value="30">Last 30 days</option>
                    <option value="90">Last 90 days</option>
                    <option value="0">All activity</option>
                  </Form.Select>
                </Form.Group>

                <div className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Verified users only"
                    checked={filters.verifiedOnly}
                    onChange={(e) => updateFilter("verifiedOnly", e.target.checked)}
                  />
                  <Form.Check
                    type="checkbox"
                    label="Active users only"
                    checked={filters.activeOnly}
                    onChange={(e) => updateFilter("activeOnly", e.target.checked)}
                  />
                  <Form.Check
                    type="checkbox"
                    label="SMS opt-in only"
                    checked={filters.optInOnly}
                    onChange={(e) => updateFilter("optInOnly", e.target.checked)}
                  />
                  <Form.Check
                    type="checkbox"
                    label="Exclude opted-out users"
                    checked={filters.excludeOptedOut}
                    onChange={(e) => updateFilter("excludeOptedOut", e.target.checked)}
                  />
                </div>

                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <Button variant="primary" onClick={() => getCustomers()}>Apply Filters</Button>
                  <Button variant="outline-secondary" onClick={() => setFilters(defaultFilters)}>Reset</Button>
                </div>
              </div>
            </Col>

            <Col lg={4}>
              <div
                style={{
                  background: "#ffffff",
                  borderRadius: 18,
                  border: "1px solid #e8edf5",
                  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.04)",
                  padding: 20,
                }}
              >
                <h5 style={{ color: "#12233d", fontWeight: 700, marginBottom: 18 }}>Audience Summary</h5>

                <Row className="g-2">
                  <Col xs={6}>
                    <div style={{ background: "#f8fbff", borderRadius: 12, padding: 12 }}>
                      <div style={{ color: "#64748b", fontSize: 12 }}>Total recipients</div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: "#0d1b2a" }}>{filteredRecipients.length}</div>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div style={{ background: "#f8fbff", borderRadius: 12, padding: 12 }}>
                      <div style={{ color: "#64748b", fontSize: 12 }}>Estimated cost</div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: "#0d1b2a" }}>${estimatedCost.toFixed(2)}</div>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div style={{ background: "#f8fbff", borderRadius: 12, padding: 12 }}>
                      <div style={{ color: "#64748b", fontSize: 12 }}>Verified</div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: "#0d1b2a" }}>
                        {filteredRecipients.filter((customer) => customer.verified).length}
                      </div>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div style={{ background: "#f8fbff", borderRadius: 12, padding: 12 }}>
                      <div style={{ color: "#64748b", fontSize: 12 }}>Opted-in</div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: "#0d1b2a" }}>
                        {filteredRecipients.filter((customer) => customer.smsConsent).length}
                      </div>
                    </div>
                  </Col>
                </Row>

                <div style={{ marginTop: 18 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 10 }}>Recipient Preview</div>
                  <div style={{ border: "1px solid #edf1f7", borderRadius: 12, overflow: "hidden" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 0.9fr 0.8fr", borderBottom: "1px solid #edf1f7", background: "#f8fbff", padding: "10px 12px", fontWeight: 700, color: "#475569", fontSize: 12 }}>
                      <span>Name</span>
                      <span>Phone</span>
                      <span>Last Ride</span>
                      <span>City</span>
                    </div>
                    {previewRecipients.length > 0 ? (
                      previewRecipients.map((customer, index) => (
                        <div key={`${customer.id}-${index}`} style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 0.9fr 0.8fr", padding: "10px 12px", borderBottom: index < previewRecipients.length - 1 ? "1px solid #edf1f7" : "none", fontSize: 12, color: "#1f2937" }}>
                          <span>{customer.name}</span>
                          <span>{customer.phone}</span>
                          <span>{customer.lastRide ? new Date(customer.lastRide).toLocaleDateString() : "—"}</span>
                          <span>{customer.city}</span>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: 14, color: "#64748b", fontSize: 12 }}>No preview rows available for the current filters.</div>
                    )}
                  </div>
                </div>
              </div>
            </Col>

            <Col lg={4}>
              <div
                style={{
                  background: "#ffffff",
                  borderRadius: 18,
                  border: "1px solid #e8edf5",
                  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.04)",
                  padding: 20,
                }}
              >
                <h5 style={{ color: "#12233d", fontWeight: 700, marginBottom: 18 }}>Message</h5>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>Template</Form.Label>
                  <Form.Select value={filters.schedule || "now"} onChange={(e) => updateFilter("schedule", e.target.value)}>
                    <option value="now">Send Now</option>
                    <option value="scheduled">Schedule Later</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>Message body</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={7}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    style={{ resize: "vertical", borderRadius: 12, border: "1px solid #dfe7f3" }}
                  />
                </Form.Group>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <span style={{ fontSize: 12, color: "#64748b" }}>Character count</span>
                  <strong style={{ fontSize: 12, color: "#0d1b2a" }}>{message.length} / 160</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
                  <span style={{ fontSize: 12, color: "#64748b" }}>Estimated segments</span>
                  <strong style={{ fontSize: 12, color: "#0d1b2a" }}>{messageSegments}</strong>
                </div>

                <div
                  style={{
                    background: "#f8fbff",
                    border: "1px solid #e8edf5",
                    borderRadius: 12,
                    padding: 12,
                    marginBottom: 12,
                    color: "#374151",
                    fontSize: 13,
                    lineHeight: 1.6,
                  }}
                >
                  {message || "Your broadcast message preview will appear here."}
                </div>

                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <Button variant="primary" style={{ flex: 1 }} onClick={handleSend}>
                    Confirm & Send
                  </Button>
                  <Button variant="outline-secondary" style={{ flex: 1 }} onClick={() => setMessage(DEFAULT_MESSAGE)}>
                    Reset
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    </DashboardLayout>
  );
};

export default BroadcastCust;
